import { BuildingSearchFilters } from '../types'
import DOMPurify from 'dompurify'

// Define valid filter keys and their expected types for buildings
const VALID_FILTER_SCHEMA: Record<
  string,
  'string' | 'number' | 'string[]' | 'number[]' | 'object' | 'boolean'
> = {
  // Location filters
  location_search: 'string',
  location_type: 'string',
  municipality: 'string',
  district: 'string',
  region: 'string',
  npa: 'string',
  city_name: 'string',
  municipality_name: 'string',
  district_name: 'string',
  region_name: 'string',
  address_full: 'string',

  // ID filters
  building_id: 'string',
  egid: 'number',
  city_id: 'string[]',
  municipality_id: 'string[]',
  district_id: 'string[]',
  region_id: 'string[]',

  // Numeric ranges
  area: 'object',
  building_year: 'object',
  floor_nb: 'object',
  unit_nb: 'object',
  height: 'object',
  building_volume: 'object',
  solar_area: 'object',
  solar_installed_power: 'object',
  solar_electrical_efficiency: 'object',
  solar_thermal_efficiency: 'object',
  energy_reference_area: 'object',
  heat_consumption_index: 'object',
  solar_installed_date: 'object',
  protected_rank: 'object',

  // Categories and types - multiple selection (arrays)
  category: 'string[]',
  type: 'string[]',
  status: 'string',
  classification: 'string',
  popety_classification: 'string[]',

  // Energy filters - single selection
  heating_type: 'string',
  energy_efficiency: 'string',
  solar_potential: 'string',

  // Environmental filters - single selection
  view_quality: 'string',
  noise_level: 'string',
  transport_accessibility: 'string',

  // Environmental features - single selection
  heater_generator: 'string',
  minergie: 'string',
  lake_view: 'string',
  mountain_view: 'string',
  public_transport_quality: 'string',

  // Boolean filters
  has_civil_protection_shelter: 'boolean',
  active: 'boolean',
  new_construction: 'boolean',
  is_furnished: 'boolean',
  pets_allowed: 'boolean',
  wheelchair_accessible: 'boolean',
  exact_location: 'boolean',

  // Date ranges
  active_listing_timestamp: 'object',
  listing_timestamp: 'object',
  construction_year: 'object',
  listing_duration: 'object',
  inactive_status_timestamp: 'object',
  buildingPermitDate: 'object',
  transaction_date: 'object',
  purchaseDate: 'object',
  rentDate: 'object',

  // Price ranges
  transactionPrice: 'object',
  purchase_price: 'object',
  rent_price: 'object',

  // User filters
  vendor_name: 'string',
  vendor_type: 'string',
  deal_type: 'string',
  title: 'string',

  // Special filters
  onlyDevelopment: 'string',

  // Geospatial
  geo_bounding_box: 'object',
  geo_polygon: 'object',
}

// Valid enum values for specific filter fields
const FILTER_ENUMS = {
  location_type: ['region', 'district', 'municipality', 'npa'],
  deal_type: ['rent', 'buy', 'both'],
  onlyDevelopment: ['display', 'hide', 'display-only'],
  // Removed category, type, and popety_classification enums as they now accept dynamic values from API
  classification: [
    'single_family',
    'multi_family',
    'apartment_building',
    'office',
    'retail',
    'warehouse',
    'factory',
    'school',
    'hospital',
    'other',
  ],
  heating_type: ['gas', 'oil', 'electric', 'heat_pump', 'district', 'other'],
  energy_efficiency: ['A', 'B', 'C', 'D', 'E', 'F', 'G'],
  solar_potential: ['excellent', 'good', 'fair', 'poor'],
  view_quality: ['excellent', 'good', 'fair', 'poor'],
  noise_level: ['low', 'medium', 'high'],
  transport_accessibility: ['excellent', 'good', 'fair', 'poor'],
  public_transport_quality: ['excellent', 'good', 'fair', 'poor'],
  minergie: ['yes', 'no', 'pending'],
  lake_view: ['yes', 'no'],
  mountain_view: ['yes', 'no'],
} as const

// Sanitize string values to prevent XSS
const sanitizeString = (value: string): string => {
  if (typeof value !== 'string') return ''

  // Use DOMPurify to clean HTML/scripts
  let cleaned: string
  try {
    cleaned = DOMPurify.sanitize(value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    })
  } catch {
    // Fallback: basic sanitization without DOMPurify
    cleaned = value.replace(/<[^>]*>/g, '')
  }

  // Additional sanitization
  return cleaned
    .trim()
    .replace(/[<>]/g, '') // Remove any remaining angle brackets
    .slice(0, 1000) // Limit length
}

// Validate if a value matches the expected type
const isValidType = (value: any, expectedType: string): boolean => {
  switch (expectedType) {
    case 'string':
      return typeof value === 'string'
    case 'number':
      return typeof value === 'number' && !isNaN(value) && isFinite(value)
    case 'boolean':
      return typeof value === 'boolean'
    case 'string[]':
      return (
        Array.isArray(value) &&
        value.every((item) => typeof item === 'string') &&
        value.length <= 100 // Reasonable limit
      )
    case 'number[]':
      return (
        Array.isArray(value) &&
        value.every(
          (item) => typeof item === 'number' && !isNaN(item) && isFinite(item)
        ) &&
        value.length <= 100 // Reasonable limit
      )
    case 'object':
      return (
        value !== null && typeof value === 'object' && !Array.isArray(value)
      )
    default:
      return false
  }
}

// Validate range objects (min/max)
const isValidRangeObject = (value: any): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const hasValidMin =
    value.min === undefined ||
    (typeof value.min === 'number' && !isNaN(value.min) && isFinite(value.min))
  const hasValidMax =
    value.max === undefined ||
    (typeof value.max === 'number' && !isNaN(value.max) && isFinite(value.max))

  // Check if min <= max when both exist
  const validRange =
    !hasValidMin ||
    !hasValidMax ||
    value.min === undefined ||
    value.max === undefined ||
    value.min <= value.max

  return hasValidMin && hasValidMax && validRange
}

// Validate date range objects
const isValidDateRangeObject = (value: any): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false

  const hasValidMin =
    value.min === undefined ||
    (typeof value.min === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.min))
  const hasValidMax =
    value.max === undefined ||
    (typeof value.max === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.max))

  return hasValidMin && hasValidMax
}

// Validate geospatial objects
const isValidGeoBoundingBox = (value: any): boolean => {
  if (!value || typeof value !== 'object') return false

  const { top_left, bottom_right } = value

  if (!top_left || !bottom_right) return false

  const isValidCoord = (coord: any) =>
    coord &&
    typeof coord.lat === 'number' &&
    isFinite(coord.lat) &&
    typeof coord.lon === 'number' &&
    isFinite(coord.lon) &&
    coord.lat >= -90 &&
    coord.lat <= 90 &&
    coord.lon >= -180 &&
    coord.lon <= 180

  return isValidCoord(top_left) && isValidCoord(bottom_right)
}

const isValidGeoPolygon = (value: any): boolean => {
  if (!value || typeof value !== 'object' || !Array.isArray(value.points))
    return false

  return (
    value.points.length >= 3 &&
    value.points.length <= 1000 && // Reasonable limit
    value.points.every(
      (point: any) =>
        point &&
        typeof point.lat === 'number' &&
        isFinite(point.lat) &&
        typeof point.lon === 'number' &&
        isFinite(point.lon) &&
        point.lat >= -90 &&
        point.lat <= 90 &&
        point.lon >= -180 &&
        point.lon <= 180
    )
  )
}

// Validate enum values
const isValidEnumValue = (key: string, value: string): boolean => {
  if (!(key in FILTER_ENUMS)) return true // No enum validation for this key

  const enumValues = FILTER_ENUMS[key as keyof typeof FILTER_ENUMS]
  return Array.isArray(enumValues) && enumValues.includes(value as never)
}

// Normalize filter values to ensure compatibility
const normalizeFilterValue = (
  key: string,
  value: any,
  expectedType: string
): any => {
  // Handle empty or invalid values
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  // Handle boolean values
  if (expectedType === 'boolean') {
    if (typeof value === 'string') {
      const lowerVal = value.toLowerCase()
      return lowerVal === 'true' || lowerVal === '1' || lowerVal === 'yes'
    }
    return Boolean(value)
  }

  // Handle string values - sanitize
  if (expectedType === 'string') {
    const sanitized = sanitizeString(String(value))

    // Validate enum values
    if (!isValidEnumValue(key, sanitized)) {
      console.warn(`Invalid enum value "${sanitized}" for filter "${key}"`)
      return undefined
    }

    return sanitized || undefined
  }

  // Handle arrays
  if (expectedType === 'string[]' || expectedType === 'number[]') {
    if (!Array.isArray(value)) {
      // Convert single value to array
      return [value]
    }
    // Filter out empty values and sanitize strings
    const filtered = value
      .filter((item) => item !== null && item !== undefined && item !== '')
      .slice(0, 100) // Limit array size

    if (expectedType === 'string[]') {
      return filtered
        .map((item) => sanitizeString(String(item)))
        .filter(Boolean)
    } else {
      return filtered
        .map((item) => {
          const num = Number(item)
          return isNaN(num) ? undefined : num
        })
        .filter((num) => num !== undefined)
    }
  }

  // Handle numeric values
  if (expectedType === 'number') {
    if (typeof value === 'string') {
      const num = parseFloat(value)
      return isNaN(num) || !isFinite(num) ? undefined : num
    }
    return typeof value === 'number' && isFinite(value) ? value : undefined
  }

  // Handle range objects (price, area, etc.)
  if (expectedType === 'object') {
    // Handle special geospatial objects
    if (key === 'geo_bounding_box') {
      return isValidGeoBoundingBox(value) ? value : undefined
    }
    if (key === 'geo_polygon') {
      return isValidGeoPolygon(value) ? value : undefined
    }

    // Handle date ranges
    if (
      key.includes('Date') ||
      key.includes('timestamp') ||
      key === 'construction_year'
    ) {
      if (isValidDateRangeObject(value)) {
        return value
      }
      // Try to convert Date objects to strings
      if (value && typeof value === 'object') {
        const result: any = {}
        if (value.min instanceof Date) {
          result.min = value.min.toISOString().split('T')[0]
        } else if (typeof value.min === 'string') {
          result.min = value.min
        }
        if (value.max instanceof Date) {
          result.max = value.max.toISOString().split('T')[0]
        } else if (typeof value.max === 'string') {
          result.max = value.max
        }
        return isValidDateRangeObject(result) ? result : undefined
      }
      return undefined
    }

    // Handle numeric ranges
    if (typeof value === 'number') {
      return { min: value, max: value }
    }

    // Ensure it's a valid range object
    if (isValidRangeObject(value)) {
      return value
    }

    return undefined
  }

  return value
}

/**
 * Parse AI assistant response to extract filter criteria
 * Handles various response formats (JSON, text, mixed)
 */
export const parseAIResponse = (
  aiResponse: string | object
): Record<string, any> => {
  try {
    // If already an object, return it
    if (typeof aiResponse === 'object' && aiResponse !== null) {
      return aiResponse as Record<string, any>
    }

    // Try to parse as JSON first
    try {
      return JSON.parse(aiResponse as string)
    } catch {
      // Fall back to text parsing
      return parseTextResponse(aiResponse as string)
    }
  } catch (error) {
    console.warn('Failed to parse AI response:', error)
    return {}
  }
}

/**
 * Parse text response using regex patterns
 */
const parseTextResponse = (text: string): Record<string, any> => {
  const filters: Record<string, any> = {}

  // Building type patterns
  const buildingTypes = [
    'residential',
    'commercial',
    'industrial',
    'public',
    'mixed',
  ]
  buildingTypes.forEach((type) => {
    const regex = new RegExp(`\\b${type}\\b`, 'i')
    if (regex.test(text)) {
      if (!filters.category) filters.category = []
      if (Array.isArray(filters.category)) {
        filters.category.push(type)
      }
    }
  })

  // Price range patterns
  const priceMatch =
    text.match(/price.*?(\d+).*?to.*?(\d+)/i) ||
    text.match(/(\d+).*?(\d+).*?(CHF|USD|EUR)/i) ||
    text.match(/budget.*?(\d+)/i)
  if (priceMatch) {
    if (priceMatch.length >= 3) {
      filters.purchase_price = {
        min: parseInt(priceMatch[1]),
        max: parseInt(priceMatch[2]),
      }
    } else if (priceMatch[1]) {
      filters.purchase_price = {
        max: parseInt(priceMatch[1]),
      }
    }
  }

  // Area patterns
  const areaMatch =
    text.match(/(\d+).*?(sqm|m2|square meters)/i) || text.match(/area.*?(\d+)/i)
  if (areaMatch) {
    filters.area = {
      min: parseInt(areaMatch[1]),
    }
  }

  // Year patterns
  const yearMatch =
    text.match(/built.*?(\d{4})/i) ||
    text.match(/year.*?(\d{4})/i) ||
    text.match(/(\d{4}).*?construction/i)
  if (yearMatch) {
    filters.building_year = {
      min: parseInt(yearMatch[1]),
    }
  }

  // Location patterns
  const locationMatch = text.match(/in\s+([A-Za-z\s,]+?)(?:\s|$|\.)/i)
  if (locationMatch) {
    filters.location_search = locationMatch[1].trim()
  }

  // Energy efficiency patterns
  const energyMatch =
    text.match(/energy.*?class.*?([A-G])/i) || text.match(/([A-G]).*?energy/i)
  if (energyMatch) {
    filters.energy_efficiency = energyMatch[1].toUpperCase()
  }

  return filters
}

/**
 * Validates and sanitizes filters received from AI search
 * Only returns filters that are compatible with the expected schema
 */
export const validateAndSanitizeFilters = (
  aiFilters: Record<string, any>
): BuildingSearchFilters => {
  const validatedFilters: BuildingSearchFilters = {}

  Object.entries(aiFilters).forEach(([key, value]) => {
    // Check if the key exists in our schema
    if (key in VALID_FILTER_SCHEMA) {
      const expectedType = VALID_FILTER_SCHEMA[key]
      const normalizedValue = normalizeFilterValue(key, value, expectedType)

      // Only add the filter if it has a valid value after normalization
      if (
        normalizedValue !== undefined &&
        isValidType(normalizedValue, expectedType)
      ) {
        // Special validation for range objects
        if (expectedType === 'object') {
          if (
            key === 'geo_bounding_box' &&
            isValidGeoBoundingBox(normalizedValue)
          ) {
            ;(validatedFilters as any)[key] = normalizedValue
          } else if (
            key === 'geo_polygon' &&
            isValidGeoPolygon(normalizedValue)
          ) {
            ;(validatedFilters as any)[key] = normalizedValue
          } else if (
            (key.includes('Date') ||
              key.includes('timestamp') ||
              key === 'construction_year') &&
            isValidDateRangeObject(normalizedValue)
          ) {
            ;(validatedFilters as any)[key] = normalizedValue
          } else if (isValidRangeObject(normalizedValue)) {
            ;(validatedFilters as any)[key] = normalizedValue
          } else {
            console.warn(
              `Filter "${key}" with value "${JSON.stringify(value)}" is not a valid object and will be ignored`
            )
          }
        } else {
          ;(validatedFilters as any)[key] = normalizedValue
        }
      } else {
        console.warn(
          `Filter "${key}" with value "${value}" is not compatible and will be ignored`
        )
      }
    } else {
      console.warn(
        `Unknown filter key "${key}" received from AI search and will be ignored`
      )
    }
  })

  return validatedFilters
}

/**
 * Merges AI filters with existing filters, ensuring compatibility
 */
export const mergeFilters = (
  existingFilters: BuildingSearchFilters,
  aiFilters: Record<string, any>
): BuildingSearchFilters => {
  const validatedAIFilters = validateAndSanitizeFilters(aiFilters)

  // Merge filters, with AI filters taking precedence
  return {
    ...existingFilters,
    ...validatedAIFilters,
  }
}

/**
 * Apply parsed and sanitized AI filters to the building search system
 */
export const applyAIFilters = (
  aiResponse: string | object,
  existingFilters: BuildingSearchFilters = {}
): {
  filters: BuildingSearchFilters
  errors: string[]
  appliedFilters: string[]
} => {
  const errors: string[] = []
  const appliedFilters: string[] = []

  try {
    // Parse the AI response
    const parsedFilters = parseAIResponse(aiResponse)

    if (Object.keys(parsedFilters).length === 0) {
      errors.push('No valid filters found in AI response')
      return { filters: existingFilters, errors, appliedFilters }
    }

    // Validate and sanitize
    const validatedFilters = validateAndSanitizeFilters(parsedFilters)

    // Track which filters were applied
    Object.keys(validatedFilters).forEach((key) => {
      appliedFilters.push(key)
    })

    // Merge with existing filters
    const mergedFilters = mergeFilters(existingFilters, validatedFilters)

    return {
      filters: mergedFilters,
      errors,
      appliedFilters,
    }
  } catch (error) {
    errors.push(
      `Failed to process AI response: ${error instanceof Error ? error.message : 'Unknown error'}`
    )
    return { filters: existingFilters, errors, appliedFilters }
  }
}
